import { useState, useEffect } from "react";
import { User, KeyRound, Shield, CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";
import { ModalShell } from "./AdminModals";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/Button";

interface AdminProfileModalProps {
  open: boolean;
  onClose: () => void;
  onProfileUpdated?: (profile: { fullName: string }) => void;
}

export function AdminProfileModal({ open, onClose, onProfileUpdated }: AdminProfileModalProps) {
  const { tokens } = useAuth();
  const token = tokens?.accessToken || "";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (!open || !token) return;
    setError("");
    setSuccess("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setLoading(true);

    api.admin.me(token)
      .then((res) => {
        setFullName(res.fullName || "");
        setPhone(res.phone || "");
        setIsSuperAdmin(res.isSuperAdmin || false);
        setPermissions(res.permissions || []);
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : "Failed to load admin profile");
      })
      .finally(() => setLoading(false));
  }, [open, token]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError("");
    setSuccess("");

    if (newPassword || currentPassword) {
      if (!currentPassword) {
        setError("Current password is required to set a new password.");
        return;
      }
      if (newPassword.length < 6) {
        setError("New password must be at least 6 characters long.");
        return;
      }
      if (newPassword !== confirmPassword) {
        setError("New password and confirmation do not match.");
        return;
      }
    }

    setSaving(true);
    try {
      const payload: {
        full_name?: string;
        current_password?: string;
        new_password?: string;
      } = {
        full_name: fullName.trim(),
      };

      if (newPassword && currentPassword) {
        payload.current_password = currentPassword;
        payload.new_password = newPassword;
      }

      const res = await api.admin.updateMe(token, payload);
      setFullName(res.fullName);
      setSuccess("Profile and password settings updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      if (onProfileUpdated) {
        onProfileUpdated({ fullName: res.fullName });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Admin Profile & Security"
      subtitle="Manage your administrator details and update your login password"
    >
      <form onSubmit={handleSave} className="space-y-6">
        {error && (
          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs">
            <AlertCircle size={15} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-success/10 border border-success/30 text-success text-xs">
            <CheckCircle size={15} className="shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-xs text-muted-foreground">Loading admin profile…</div>
        ) : (
          <>
            {/* Account Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-1 border-b border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <User size={14} className="text-primary" />
                Profile Information
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-xs font-medium space-y-1">
                  <span>Full name</span>
                  <input
                    type="text"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm"
                    placeholder="Administrator Name"
                  />
                </label>

                <div className="block text-xs font-medium space-y-1">
                  <span className="text-muted-foreground">Phone number</span>
                  <input
                    type="text"
                    disabled
                    value={phone}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-muted/40 text-sm text-muted-foreground cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1 text-xs">
                <span className="font-medium text-muted-foreground">Role:</span>
                <span className="inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  <Shield size={11} />
                  {isSuperAdmin ? "Super Admin" : "Administrator"}
                </span>
                {permissions.length > 0 && (
                  <span className="text-muted-foreground">
                    ({permissions.length} permissions assigned)
                  </span>
                )}
              </div>
            </div>

            {/* Password Change */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2 pb-1 border-b border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <KeyRound size={14} className="text-primary" />
                Change Password
              </div>
              <p className="text-xs text-muted-foreground">
                Leave password fields blank if you only want to update your name.
              </p>

              <div className="space-y-3">
                <label className="block text-xs font-medium space-y-1">
                  <span>Current password</span>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="w-full px-3 py-2 pr-9 rounded-xl border border-border bg-background text-sm"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block text-xs font-medium space-y-1">
                    <span>New password</span>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="At least 6 characters"
                        minLength={6}
                        className="w-full px-3 py-2 pr-9 rounded-xl border border-border bg-background text-sm"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((v) => !v)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showNewPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </label>

                  <label className="block text-xs font-medium space-y-1">
                    <span>Confirm new password</span>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm"
                      autoComplete="new-password"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 rounded-xl border border-border text-xs font-semibold hover:bg-muted"
              >
                Cancel
              </button>
              <Button
                type="submit"
                loading={saving}
                loadingText="Updating…"
                variant="primary"
                className="px-5 py-2 text-xs"
              >
                Save Changes
              </Button>
            </div>
          </>
        )}
      </form>
    </ModalShell>
  );
}
